import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import styles from './CategoriesView.module.css';
import { Edit, Plus, Search, Trash, Globe } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useCategories } from '../../../../hooks/categories/useCategories';
import Loading from '../../../../common/HousGlass Loader/Loading';

const LANGUAGES = [
  { value: 'english', label: 'English' },
  { value: 'hindi', label: 'Hindi' },
  { value: 'garhwali', label: 'Garhwali' },
  { value: 'kumaoni', label: 'Kumaoni' },
  { value: 'marathi', label: 'Marathi' }
];

const CategoryModal = ({ isOpen, onClose, title, categoryData = { name: '', language: 'english' }, onSubmit }) => {
  const [name, setName] = useState(categoryData?.name || '');
  const [language, setLanguage] = useState(categoryData?.language || 'english');

  // Update form when category data changes
  useEffect(() => {
    if (categoryData) {
      setName(categoryData.name || '');
      setLanguage(categoryData.language || 'english');
    }
  }, [categoryData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Category name is required');
      return;
    }
    onSubmit({ name, language });
    setName('');
    setLanguage('english');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <h2>{title}</h2>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Category Name"
            className={styles.modalInput}
          />
          
          <div className={styles.formGroup}>
            <label htmlFor="language">Language:</label>
            <select
              id="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className={styles.modalSelect}
            >
              {LANGUAGES.map(lang => (
                <option key={lang.value} value={lang.value}>{lang.label}</option>
              ))}
            </select>
          </div>
          
          <div className={styles.modalButtons}>
            <button type="submit" className={styles.addButton}>
              {title === 'Add Category' ? 'Add' : 'Update'}
            </button>
            <button type="button" onClick={onClose} className={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <h2>Confirm Delete</h2>
        <p>Are you sure you want to delete this category?</p>
        <div className={styles.modalButtons}>
          <button onClick={onConfirm} className={styles.deleteButton}>
            Delete
          </button>
          <button onClick={onClose} className={styles.cancelButton}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const CategoriesView = () => {
  const { category } = useParams();
  const [selectedLanguage, setSelectedLanguage] = useState('english');
  
  const { 
    categories, 
    isLoading, 
    error, 
    createCategory, 
    updateCategory, 
    deleteCategory 
  } = useCategories(category, selectedLanguage);

  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleAdd = async ({ name, language }) => {
    try {
      await createCategory({ name, language });
      toast.success('Category added successfully!');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add category');
    }
  };

  const handleEdit = async ({ name, language }) => {
    try {
      await updateCategory({
        categoryId: selectedCategory.id,
        name,
        language
      });
      setIsEditModalOpen(false);
      toast.success('Category updated successfully!');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update category');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCategory(selectedCategory.id);
      setIsDeleteModalOpen(false);
      toast.success('Category deleted successfully!');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete category');
    }
  };

  const filteredCategories = categories?.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (isLoading) {
    return <div className={styles.loading}><Loading/></div>;
  }

  if (error) {
    return <div className={styles.error}>Error: {error}</div>;
  }

  return (
    <div className={styles.container}>
      <h2 style={{ color:'white' }}>{category?.charAt(0).toUpperCase() + category?.slice(1)} Categories</h2>
      
      <div className={styles.header}>
        <div className={styles.headerActions}>
          <button
            className={styles.addButton}
            onClick={() => setIsAddModalOpen(true)}
          >
            <Plus /> Add Category
          </button>
          
          <div className={styles.languageFilter}>
            <Globe className={styles.globeIcon} />
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className={styles.languageSelect}
            >
              {LANGUAGES.map(lang => (
                <option key={lang.value} value={lang.value}>{lang.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className={styles.searchBar}>
          <Search className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.categoriesList}>
        {filteredCategories.length === 0 ? (
          <div className={styles.noCategories}>
            {searchTerm 
              ? 'No categories found' 
              : `No ${selectedLanguage} categories available for ${category}`}
          </div>
        ) : (
          filteredCategories.map((category) => (
            <div key={category.id} className={styles.categoryItem}>
              <div className={styles.categoryInfo}>
                <span className={styles.categoryName}>{category.name}</span>
                <span className={styles.categoryLanguage}>{category.language}</span>
              </div>
              <div className={styles.actions}>
                <button
                  className={styles.iconButton}
                  onClick={() => {
                    setSelectedCategory(category);
                    setIsEditModalOpen(true);
                  }}
                >
                  <Edit />
                </button>
                <button
                  className={styles.iconButton}
                  onClick={() => {
                    setSelectedCategory(category);
                    setIsDeleteModalOpen(true);
                  }}
                >
                  <Trash />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <CategoryModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add Category"
        categoryData={{ name: '', language: selectedLanguage }}
        onSubmit={handleAdd}
      />

      <CategoryModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Category"
        categoryData={selectedCategory}
        onSubmit={handleEdit}
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default CategoriesView;